import React from "react";
import PropTypes from "prop-types";
import { Empty, Tooltip } from "antd";
import { formatMoney } from "../money-format";
import { isBlank } from "../data-utils";

const DASH = "—";

function getValue(row, col) {
    if (!row) {
        return undefined;
    }
    if (col.path) {
        return col.key
            .split(".")
            .reduce((obj, key) => (obj == null ? obj : obj[key]), row);
    }
    return row[col.key];
}

function toNumber(value) {
    const n = Number(String(value == null ? "" : value).replace(/[^0-9.-]/g, ""));
    return isFinite(n) && !Number.isNaN(n) ? n : NaN;
}

// PAR cells mirror the legacy Portfolio View: show the amount with its share of
// AUM, and flag red when that share breaches the column threshold (PAR 0/30 > 5%,
// PAR 90 > 1%). Returns { text, red } for the renderer.
function formatPar(value, row, col) {
    const amount = toNumber(value);
    if (Number.isNaN(amount)) {
        return { text: DASH, red: false };
    }
    const aum = toNumber(getValue(row, { key: col.ratioKey || "aum" }));
    const ratio =
        !Number.isNaN(aum) && aum ? (amount / aum) * 100 : null;
    const amountText = amount.toLocaleString("en-IN", {
        maximumFractionDigits: 2
    });
    const text =
        ratio !== null ? `${amountText} (${ratio.toFixed(2)}%)` : amountText;
    const red = ratio !== null && ratio > (col.threshold || 0);
    return { text, red };
}

function formatCell(value, col, unit) {
    if (isBlank(value)) {
        return DASH;
    }
    if (col.date) {
        // ISO (YYYY-MM-DD…) → DD/MM/YYYY; leave anything else untouched.
        const parts = String(value).slice(0, 10).split("-");
        return parts.length === 3
            ? `${parts[2]}/${parts[1]}/${parts[0]}`
            : String(value);
    }
    if (col.money) {
        return formatMoney(value, unit || "full") || DASH;
    }
    if (col.number) {
        const n = Number(String(value).replace(/[^0-9.-]/g, ""));
        return isFinite(n) && !Number.isNaN(n)
            ? n.toLocaleString("en-IN", { maximumFractionDigits: 2 })
            : String(value);
    }
    if (col.percent) {
        const n = Number(String(value).replace(/[^0-9.-]/g, ""));
        return isFinite(n) && !Number.isNaN(n)
            ? `${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })} %`
            : String(value);
    }
    return String(value);
}

function tooltipText(row, col) {
    if (!col.tooltipKey) {
        return null;
    }
    const value = getValue(row, { key: col.tooltipKey });
    return isBlank(value) ? null : String(value);
}

function statusTone(value) {
    const v = String(value || "").toLowerCase();
    if (["closed", "rejected", "hold"].some(s => v.indexOf(s) !== -1)) {
        return "bad";
    }
    if (["active", "open"].some(s => v.indexOf(s) !== -1)) {
        return "good";
    }
    return "muted";
}

/**
 * Generic read-only table for the Existing Portfolio section, driven by a column
 * config ({ key, label, money, number, path, status }).
 *
 * `stickyCount` freezes that many leading columns against the left edge while
 * the remaining columns scroll horizontally under them. The left offsets are
 * MEASURED from the rendered header cells rather than derived from the declared
 * `width`s: the table lays out `auto`, so a column's real width is whatever its
 * content needs, and offsets computed from the declared widths would drift and
 * let the frozen block overlap the first scrolling column.
 */
const PortfolioTable = ({
    columns,
    rows,
    emptyText,
    unit,
    totalRow,
    stickyCount
}) => {
    const frozen = Number(stickyCount) || 0;
    const headRefs = React.useRef([]);
    const [offsets, setOffsets] = React.useState([]);

    React.useLayoutEffect(() => {
        if (!frozen) {
            return undefined;
        }
        const measure = () => {
            const next = [];
            let left = 0;
            for (let i = 0; i < frozen; i += 1) {
                next.push(left);
                const el = headRefs.current[i];
                left += el ? el.getBoundingClientRect().width : 0;
            }
            // Only commit a real change, else the state update re-renders into
            // the same measurement forever.
            setOffsets(prev =>
                prev.length === next.length &&
                prev.every((v, i) => Math.abs(v - next[i]) < 0.5)
                    ? prev
                    : next
            );
        };
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    });

    if (!rows || !rows.length) {
        // Compact one-line empty state — antd's default `<Empty>` mailbox
        // illustration eats a full card height for no signal. Matches the
        // Group Info compact empty-state style.
        return (
            <div className={"cc-portfolio-empty"}>
                {emptyText || "No data"}
            </div>
        );
    }
    // First column that carries a header label — the "Total" footer label goes
    // here so a leading icon-only column stays narrow.
    const labelIdx = columns.findIndex(c => c.label);
    const totalLabelIndex = labelIdx === -1 ? 0 : labelIdx;
    // Merges the sticky class + left offset into whatever a cell already had.
    // Until the first measurement lands, the offsets are unknown — leave the
    // cells unpinned rather than pin them at a wrong position.
    const stick = (ci, className, style) => {
        if (ci >= frozen || offsets[ci] === undefined) {
            return { className: className || "", style };
        }
        return {
            className:
                (className ? className + " " : "") +
                "cc-pf-sticky" +
                (ci === frozen - 1 ? " cc-pf-sticky--edge" : ""),
            style: { ...(style || {}), left: offsets[ci] }
        };
    };
    return (
        <div className={"cc-pf-table-wrap"}>
            <table
                className={
                    "cc-pf-table" + (frozen ? " cc-pf-table--sticky" : "")
                }
            >
                <thead>
                    <tr>
                        {columns.map((col, ci) => (
                            <th
                                key={col.key}
                                ref={el => {
                                    headRefs.current[ci] = el;
                                }}
                                {...stick(
                                    ci,
                                    "",
                                    col.width ? { width: col.width } : undefined
                                )}
                            >
                                {col.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, i) => (
                        <tr key={i}>
                            {columns.map((col, ci) => {
                                const value = getValue(row, col);
                                if (col.render) {
                                    const tip = tooltipText(row, col);
                                    const rendered = col.render(value, row);
                                    return (
                                        <td
                                            key={col.key}
                                            {...stick(
                                                ci,
                                                col.cellClassName || "",
                                                col.width
                                                    ? { width: col.width }
                                                    : undefined
                                            )}
                                        >
                                            {tip ? (
                                                <Tooltip
                                                    title={tip}
                                                    mouseEnterDelay={0}
                                                    overlayStyle={{
                                                        maxWidth: 320,
                                                        fontSize: 12
                                                    }}
                                                >
                                                    <span>{rendered}</span>
                                                </Tooltip>
                                            ) : (
                                                rendered
                                            )}
                                        </td>
                                    );
                                }
                                if (col.par) {
                                    const par = formatPar(value, row, col);
                                    return (
                                        <td
                                            key={col.key}
                                            className={
                                                "cc-pf-num" +
                                                (par.red ? " cc-pf-red" : "")
                                            }
                                        >
                                            {par.text}
                                        </td>
                                    );
                                }
                                if (col.status) {
                                    return (
                                        <td key={col.key}>
                                            <span
                                                className={
                                                    "cc-pf-status cc-pf-status--" +
                                                    statusTone(value)
                                                }
                                            >
                                                {value || DASH}
                                            </span>
                                        </td>
                                    );
                                }
                                const cellText = formatCell(value, col, unit);
                                // Frozen cells clip long values. Surface the full
                                // text in a Tooltip (native `title` has a fixed
                                // ~0.7s browser delay); shown only for clipped
                                // string cells so the generic table is untouched.
                                const clipped =
                                    ci < frozen &&
                                    typeof cellText === "string" &&
                                    cellText &&
                                    cellText !== DASH;
                                const tip =
                                    tooltipText(row, col) ||
                                    (clipped || col.truncate ? cellText : null);
                                return (
                                    <td
                                        key={col.key}
                                        {...stick(
                                            ci,
                                            col.money ||
                                                col.number ||
                                                col.percent
                                                ? "cc-pf-num"
                                                : "",
                                            col.width || col.wrap
                                                ? {
                                                    width: col.width,
                                                    maxWidth: col.width,
                                                    whiteSpace: col.wrap
                                                        ? "normal"
                                                        : undefined,
                                                    wordBreak: col.wrap
                                                        ? "break-word"
                                                        : undefined
                                                }
                                                : undefined
                                        )}
                                    >
                                        {tip ? (
                                            <Tooltip
                                                title={tip}
                                                mouseEnterDelay={0.15}
                                            >
                                                <span
                                                    className={
                                                        col.truncate
                                                            ? "cc-pf-trunc-cell"
                                                            : undefined
                                                    }
                                                    style={
                                                        col.truncate
                                                            ? { maxWidth: col.width }
                                                            : undefined
                                                    }
                                                >
                                                    {cellText}
                                                </span>
                                            </Tooltip>
                                        ) : (
                                            cellText
                                        )}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
                {totalRow ? (
                    <tfoot>
                        <tr className={"cc-pf-total"}>
                            {columns.map((col, ci) => {
                                // Put the "Total" label in the first labelled
                                // column so a leading icon-only column (e.g. an
                                // edit action) is not widened to fit the text.
                                if (ci === totalLabelIndex) {
                                    return (
                                        <td
                                            key={col.key}
                                            {...stick(ci, "cc-pf-total__label")}
                                        >
                                            {totalRow._label || "Total"}
                                        </td>
                                    );
                                }
                                const value = getValue(totalRow, col);
                                const isNum =
                                    col.money || col.number || col.percent;
                                if (
                                    value === undefined ||
                                    value === null ||
                                    value === ""
                                ) {
                                    return (
                                        <td
                                            key={col.key}
                                            {...stick(
                                                ci,
                                                isNum ? "cc-pf-num" : ""
                                            )}
                                        />
                                    );
                                }
                                return (
                                    <td
                                        key={col.key}
                                        {...stick(ci, isNum ? "cc-pf-num" : "")}
                                    >
                                        {formatCell(value, col, unit)}
                                    </td>
                                );
                            })}
                        </tr>
                    </tfoot>
                ) : null}
            </table>
        </div>
    );
};

PortfolioTable.propTypes = {
    columns: PropTypes.array.isRequired,
    rows: PropTypes.array,
    emptyText: PropTypes.string,
    unit: PropTypes.string,
    totalRow: PropTypes.object,
    stickyCount: PropTypes.number
};

export default PortfolioTable;
